import { useState, useCallback, useMemo } from 'react';
import type { DisplayFormData, DisplayFormField } from '../../standalone/types';
import { expandCountry } from '../../countryData';
import { usePrimaryFill } from '../../hooks/usePrimaryFill';
import { useT } from '../../i18n';
import { CountryPicker } from './CountryPicker';
import { PhoneInput } from './PhoneInput';
import styles from './DynamicFormDrawer.module.css';

interface DynamicFormDrawerProps {
  activeForm: DisplayFormData | null;
  onClose: () => void;
  onSubmit: (formId: string, values: Record<string, unknown>) => Promise<void>;
  isDisabled?: boolean;
  primaryColor?: string;
  primaryHoverColor?: string;
  useGradient?: boolean;
  /** Flow inline and grow to fit the form (searchbar layout) instead of overlaying. */
  inline?: boolean;
  /** Show the header in inline mode, where the surrounding UI usually labels the form. */
  showHeader?: boolean;
  /** Whether the header has a back button; false when there is nothing to return to. */
  showBackButton?: boolean;
}

type ResultState = 'idle' | 'submitting' | 'success' | 'error';

function getInitialFormData(fields: DisplayFormField[]): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  fields.forEach((field) => {
    const def = field.default_value;
    if (field.field_type === 'multi_select') {
      data[field.name] = Array.isArray(def) ? def : [];
    } else if (field.field_type === 'checkbox') {
      data[field.name] = typeof def === 'boolean' ? def : false;
    } else if (field.field_type === 'phone') {
      // The phone default only preselects the dial prefix (via PhoneInput's
      // defaultCountry prop) — the field value stays empty so a required
      // phone still requires the user to enter a number.
      data[field.name] = '';
    } else {
      data[field.name] = typeof def === 'string' ? def : '';
    }
  });
  return data;
}

/** The form body; remounted via `key` when the form changes. */
function FormContent({
  form,
  onClose,
  onSubmit,
  isDisabled,
  primaryColor,
  primaryHoverColor,
  useGradient = false,
  inline = false,
  showHeader = false,
  showBackButton = true,
}: {
  form: DisplayFormData;
  onClose: () => void;
  onSubmit: (formId: string, values: Record<string, unknown>) => Promise<void>;
  isDisabled: boolean;
  primaryColor?: string;
  primaryHoverColor?: string;
  useGradient?: boolean;
  inline?: boolean;
  showHeader?: boolean;
  showBackButton?: boolean;
}) {
  const t = useT();
  // Initialized once: the component remounts when its key changes.
  const initialData = useMemo(() => getInitialFormData(form.fields), [form.fields]);
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resultState, setResultState] = useState<ResultState>('idle');

  const validateField = useCallback((field: DisplayFormField, value: unknown): string | null => {
    if (field.is_required) {
      // A required checkbox must be checked (true) — an unchecked box is false,
      // which would otherwise slip past the empty-value checks below.
      if (field.field_type === 'checkbox') {
        if (value !== true) {
          return t('form.required');
        }
      } else if (value === null || value === undefined || value === '') {
        return t('form.required');
      } else if (Array.isArray(value) && value.length === 0) {
        return t('form.required');
      }
    }

    const stringValue = typeof value === 'string' ? value : '';

    if (field.field_type === 'email' && stringValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        return t('form.invalidEmail');
      }
    }

    if (field.field_type === 'phone' && stringValue) {
      const phoneRegex = /^[0-9+\-\s()]+$/;
      if (!phoneRegex.test(stringValue)) {
        return t('form.invalidPhone');
      }
    }

    return null;
  }, [t]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    form.fields.forEach((field) => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [form.fields, formData, validateField]);

  // Whether every required field has a value. Drives the submit button's
  // disabled state — format validation (email/phone) still runs on submit.
  const requiredFieldsFilled = useMemo(
    () =>
      form.fields.every((field) => {
        if (!field.is_required) return true;
        const value = formData[field.name];
        if (field.field_type === 'checkbox') return value === true;
        if (field.field_type === 'multi_select') {
          return Array.isArray(value) && value.length > 0;
        }
        return value !== null && value !== undefined && value !== '';
      }),
    [form.fields, formData],
  );

  const submitDisabled =
    resultState === 'submitting' || isDisabled || !requiredFieldsFilled;

  const { style: submitFillStyle, onMouseEnter, onMouseLeave } = usePrimaryFill(
    !submitDisabled ? primaryColor : undefined,
    primaryHoverColor,
    useGradient,
  );

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setResultState('submitting');

    // Country fields store the ISO alpha-2 code internally (the picker matches
    // selection by code). On submit, expand each into an object carrying all
    // three representations so downstream consumers (such as Salesforce) can pick
    // the one they need; a Brain client sends the code back (`withCountryCodes`).
    // formData itself stays untouched.
    const payload: Record<string, unknown> = { ...formData };
    form.fields.forEach((field) => {
      if (field.field_type === 'country') {
        const code = payload[field.name];
        if (typeof code === 'string' && code) {
          payload[field.name] = expandCountry(code);
        }
      }
    });

    try {
      await onSubmit(form.form_id, payload);
      setResultState('success');

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setResultState('error');

      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  const handleFieldChange = (fieldName: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const renderField = (field: DisplayFormField) => {
    const value = formData[field.name];
    const error = errors[field.name];
    const inputClassName = `${styles.input} ${error ? styles.hasError : ''}`;

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || ''}
            className={inputClassName}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || ''}
            className={inputClassName}
          />
        );

      case 'phone':
        return (
          <PhoneInput
            value={(value as string) ?? ''}
            onChange={(val) => handleFieldChange(field.name, val)}
            placeholder={field.placeholder || undefined}
            hasError={!!error}
            defaultCountry={
              typeof field.default_value === 'string' ? field.default_value : undefined
            }
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || ''}
            className={inputClassName}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={4}
            className={`${inputClassName} ${styles.textarea}`}
          />
        );

      case 'select':
        return (
          <select
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={`${inputClassName} ${styles.select}`}
          >
            <option value="">
              {field.placeholder || t('form.selectOption')}
            </option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        return (
          <div className={styles.optionsGroup}>
            {field.options?.map((opt) => {
              const currentValues = (value as string[]) || [];
              const isSelected = currentValues.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const newValues = isSelected
                      ? currentValues.filter((v) => v !== opt.value)
                      : [...currentValues, opt.value];
                    handleFieldChange(field.name, newValues);
                  }}
                  className={`${styles.optionButton} ${isSelected ? styles.selected : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );

      case 'radio':
        return (
          <div className={styles.optionsGroup}>
            {field.options?.map((opt) => {
              const isSelected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleFieldChange(field.name, opt.value)}
                  className={`${styles.optionButton} ${isSelected ? styles.selected : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );

      case 'checkbox': {
        // No placeholder \u2192 compact layout: a small box at the start with the
        // field label to its right (e.g. terms acceptance). The separate field
        // label above is suppressed for this case (see fieldWrapper below).
        if (!field.placeholder) {
          return (
            <button
              type="button"
              onClick={() => handleFieldChange(field.name, !value)}
              className={styles.checkboxCompact}
            >
              <span
                className={`${styles.checkboxBox} ${value ? styles.checkboxBoxChecked : ''} ${
                  error ? styles.checkboxBoxError : ''
                }`}
              >
                {value ? '\u2713' : ''}
              </span>
              <span className={styles.checkboxCompactLabel}>
                {field.label}
                {field.is_required && <span className={styles.requiredStar}>*</span>}
              </span>
            </button>
          );
        }
        return (
          <button
            type="button"
            onClick={() => handleFieldChange(field.name, !value)}
            className={`${styles.checkboxButton} ${value ? styles.checked : ''}`}
          >
            <span>{field.placeholder}</span>
            <span className={styles.checkmark}>{value ? '\u2713' : ''}</span>
          </button>
        );
      }

      case 'date':
        return (
          <input
            type="date"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={inputClassName}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={inputClassName}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={(value as string) ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={inputClassName}
          />
        );

      case 'country':
        return (
          <CountryPicker
            value={(value as string) ?? ''}
            onChange={(code) => handleFieldChange(field.name, code)}
            placeholder={field.placeholder || undefined}
            className={styles.input}
            hasError={!!error}
          />
        );

      default:
        return null;
    }
  };

  if (resultState === 'success' || resultState === 'error') {
    return (
      <div className={styles.resultScreen}>
        <div className={styles.resultContent}>
          <div className={`${styles.resultIcon} ${resultState === 'success' ? styles.success : styles.error}`}>
            {resultState === 'success' ? (
              <svg className={styles.resultIconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className={styles.resultIconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <p className={styles.resultMessage}>
            {resultState === 'success'
              ? t('form.success')
              : t('form.error')}
          </p>
        </div>
      </div>
    );
  }

  const rootClass = inline
    ? `${styles.drawer} ${styles.drawerInline}${showHeader ? ` ${styles.drawerInlineWithHeader}` : ''}`
    : styles.drawer;

  return (
    <div className={rootClass}>
      <div className={styles.header}>
        {showBackButton && (
          <button
            onClick={onClose}
            className={styles.backButton}
            aria-label={t('form.close')}
          >
            <svg className={styles.backIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <span className={styles.title}>{form.title}</span>
      </div>

      <div className={inline ? `${styles.content} ${styles.contentInline}` : styles.content}>
        <div className={`${styles.formContent} ${styles.fadeIn}`}>
          {form.description && (
            <div className={styles.description}>
              <p className={styles.descriptionText}>{form.description}</p>
            </div>
          )}

          {[...form.fields]
            .sort((a, b) => a.order - b.order)
            .map((field) => {
              const fullWidth =
                field.field_type === 'textarea' ||
                field.field_type === 'multi_select' ||
                field.field_type === 'radio' ||
                field.field_type === 'checkbox';
              // A placeholder-less checkbox renders its own inline label next to
              // the box, so skip the standalone label above it.
              const isCompactCheckbox =
                field.field_type === 'checkbox' && !field.placeholder;
              return (
                <div
                  key={field.id}
                  className={`${styles.fieldWrapper} ${fullWidth ? styles.fieldFullWidth : ''}`}
                >
                  {!isCompactCheckbox && (
                    <label className={styles.fieldLabel}>
                      {field.label}
                      {field.is_required && <span className={styles.requiredStar}>*</span>}
                    </label>
                  )}
                  {renderField(field)}
                  {field.help_text && (
                    <span className={styles.helpText}>{field.help_text}</span>
                  )}
                  {errors[field.name] && (
                    <span className={styles.errorText}>{errors[field.name]}</span>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <div className={inline ? `${styles.footer} ${styles.footerInline}` : styles.footer}>
        <button
          onClick={handleSubmit}
          disabled={submitDisabled}
          className={styles.submitButton}
          style={submitFillStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {resultState === 'submitting' ? t('form.submitting') : t('form.submit')}
        </button>
      </div>
    </div>
  );
}

/** Renders a form received from the DISPLAY_FORM tool. */
export function DynamicFormDrawer({
  activeForm,
  onClose,
  onSubmit,
  isDisabled = false,
  primaryColor,
  primaryHoverColor,
  useGradient = false,
  inline = false,
  showHeader = false,
  showBackButton = true,
}: DynamicFormDrawerProps) {
  if (!activeForm) return null;

  return (
    <FormContent
      key={activeForm.render_event_id ?? activeForm.form_id}
      form={activeForm}
      onClose={onClose}
      onSubmit={onSubmit}
      isDisabled={isDisabled}
      primaryColor={primaryColor}
      primaryHoverColor={primaryHoverColor}
      useGradient={useGradient}
      inline={inline}
      showHeader={showHeader}
      showBackButton={showBackButton}
    />
  );
}

export default DynamicFormDrawer;
